import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import { typeLabels, type MovieType } from '@/mocks/movies';
import { filterAndSortMovies, PAGE_SIZE } from '@/utils/movie';
import FilterPanel, { SortBar } from './components/FilterPanel';
import Pagination from './components/Pagination';
import type { Filters } from './components/FilterPanel';

const introMap: Record<MovieType, { desc: string; keywords: string }> = {
  movie: {
    desc: '剧情长片、类型佳作与年度新片汇于一栏，按地区、年份与评分筛选，找到今晚想看的那一部。',
    keywords: '电影,剧情片,新片,电影推荐',
  },
  tv: {
    desc: '都市、家庭、悬疑与古装长剧集集中在这里，更新进度清晰可见，追剧从不错过下一集。',
    keywords: '电视剧,剧集,追剧,连续剧推荐',
  },
  variety: {
    desc: '美食、音乐、竞技与访谈节目，慢综艺与热闹现场都有，为日常留一点轻松的空白。',
    keywords: '综艺,综艺节目,真人秀,慢综艺',
  },
};

const bannerMap: Record<MovieType, string> = {
  movie:
    'https://readdy.ai/api/search-image?query=Cinematic%20abstract%20banner%20of%20warm%20amber%20projector%20light%20through%20dust%20in%20a%20dark%20empty%20cinema%2C%20minimal%20elegant%20composition%2C%20soft%20haze&width=1600&height=400&seq=sigua-banner-movie&orientation=landscape',
  tv:
    'https://readdy.ai/api/search-image?query=Cinematic%20abstract%20banner%20of%20a%20quiet%20living%20room%20at%20night%20with%20warm%20lamp%20glow%20and%20soft%20curtain%20shadows%2C%20minimal%20elegant%20composition&width=1600&height=400&seq=sigua-banner-tv&orientation=landscape',
  variety:
    'https://readdy.ai/api/search-image?query=Cinematic%20abstract%20banner%20of%20a%20warm%20stage%20with%20amber%20spotlight%20beams%20and%20floating%20dust%20particles%2C%20minimal%20elegant%20stage%20composition&width=1600&height=400&seq=sigua-banner-variety&orientation=landscape',
};

const defaultFilters: Filters = {
  region: '全部',
  year: '全部',
  minRating: 0,
  sort: 'new',
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const validType = (['movie', 'tv', 'variety'].includes(type ?? '') ? type : 'movie') as MovieType;

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, [validType]);

  const result = useMemo(
    () =>
      filterAndSortMovies({
        type: validType,
        region: filters.region,
        year: filters.year,
        minRating: filters.minRating,
        sort: filters.sort,
      }),
    [validType, filters],
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeCount =
    (filters.region !== '全部' ? 1 : 0) +
    (filters.year !== '全部' ? 1 : 0) +
    (filters.minRating > 0 ? 1 : 0);

  const updateFilters = (next: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  const label = typeLabels[validType];
  const intro = introMap[validType];

  return (
    <SiteLayout
      title={`丝瓜视频 - ${label}分类`}
      description={`丝瓜视频${label}频道：${intro.desc}`}
      keywords={`丝瓜视频,${label},${intro.keywords}`}
    >
      <main className="flex-1 w-full pb-10">
        <section className="relative w-full h-48 md:h-60 overflow-hidden bg-foreground-950">
          <img
            src={bannerMap[validType]}
            alt={`${label}频道背景`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10"></div>
          <div className="relative h-full px-4 md:px-8 flex flex-col justify-end pb-6 md:pb-8">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-white/65 mb-2 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>分类</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{label}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-white tracking-tight">
              {label}频道
            </h1>
            <p className="mt-2 text-xs md:text-sm text-white/75 max-w-2xl leading-relaxed">
              {intro.desc}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-6 md:mt-8">
          <div className="flex items-center gap-1.5 mb-4 overflow-x-auto no-scrollbar">
            {(['movie', 'tv', 'variety'] as MovieType[]).map((item) => (
              <Link
                key={item}
                to={`/category/${item}`}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  item === validType
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {typeLabels[item]}
              </Link>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
            <aside className="hidden lg:block w-[280px] shrink-0">
              <div className="sticky top-20">
                <FilterPanel
                  filters={filters}
                  onChange={updateFilters}
                  onReset={resetFilters}
                  activeCount={activeCount}
                />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="lg:hidden mb-3">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="w-full h-10 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选条件
                  {activeCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-primary-500 text-background-50 text-[10px] font-bold">
                      {activeCount}
                    </span>
                  )}
                </button>
              </div>

              <SortBar sort={filters.sort} onChange={(sort) => updateFilters({ sort })} total={result.length} />

              {paged.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                    {paged.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                  <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
                </>
              ) : (
                <EmptyState
                  title="没有找到符合条件的作品"
                  description="当前筛选组合下暂时没有内容，试试放宽年份或评分条件，或者直接重置所有筛选。"
                  actionLabel="重置筛选条件"
                  onAction={resetFilters}
                  secondaryLabel="返回首页"
                  secondaryTo="/"
                />
              )}
            </div>
          </div>
        </section>
      </main>

      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex items-end">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-black/50 cursor-pointer"
          />
          <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4 animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={updateFilters}
              onReset={resetFilters}
              activeCount={activeCount}
            />
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-11 rounded-md border border-background-300 text-sm font-medium text-foreground-800 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-bold cursor-pointer"
              >
                查看 {result.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}